import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import usePopover from "components/common/usePopover";
import useIsMobile from "mobile/useIsMobile";
import MOBILE from "mobile/mobileConfig";
import BottomSheet from "mobile/components/BottomSheet";
import LabTestService from "../service/LabTestService";

/**
 * "Search & add investigations" — Figma 3500:14515.
 *
 * A MULTI-SELECT popover over a two-level catalogue: each matching panel, with
 * its sub-tests indented beneath. Ticking a panel orders the whole panel;
 * ticking a child orders that analyte. Nothing is added until "Add selected".
 *
 * THREE calls, then one:
 *   GET  /dt/labtest/search?queryString=          panels whose NAME matched
 *   GET  /dt/labtestmapping/search?queryString=   sub-tests whose name or alias
 *                                                 matched, each carrying its
 *                                                 PARENT panel — this is what
 *                                                 makes "hemo" surface CBC
 *   POST /dt/labtest {ltCodes:[…]}                every panel's full child list
 *                                                 in ONE round-trip
 *
 * The board's "Panel · 12 tests" is now literal: the count is the panel's true
 * child count, not how many matched. EVERY panel on screen lists its whole
 * child list — how a panel was found does not change what it contains. The
 * count doubles as a collapse, folding a panel down to just the rows the query
 * matched; the board draws no such affordance, but it also assumed a panel was
 * always small enough to show whole, which a 38-test Urine Routine is not.
 *
 * The popover has no search row of its own: the card's field sits directly
 * above and already shows the query.
 *
 * TWO THINGS THAT LOOK LIKE BUGS AND ARE NOT:
 *  - A sub-test name can appear under two different panels with two different
 *    codes. An SLABT identifies a (panel, analyte) PAIR, not a clinical
 *    concept — "Hemoglobin" in CBC and in an anaemia profile are genuinely
 *    different rows. So children are deduped WITHIN a panel, never across.
 *  - The synonym map's `label` is the alias that matched ("Hb"), not the
 *    canonical name. Every displayed and saved label is resolved back through
 *    the catalogue's own child list; the alias is used only to decide WHICH
 *    child matched, and shown only if the catalogue has no name for that code.
 *
 * AND ONE THAT IS A BUG, IN THE DATA: the synonym map's `mainTestName` is stale.
 * On dev1 it calls LABT284 "Hemoglobin electrophrosesis" while the catalogue
 * calls it "X-Ray Ankle Lateral". So NO panel name is ever taken from the map —
 * a synonym-only panel is held back until the catalogue names it, and dropped
 * outright if the catalogue has never heard of the code.
 */
const DEBOUNCE_MS = 250;
/**
 * "hem" alone reaches 14 panels through the synonym map. Cap the list, and say
 * so on screen — a silently truncated result reads as "that's all there is".
 */
const MAX_PANELS = 12;

const Tick = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
    <path d="M3.5 8.4l3 3 6-6.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** The catalogue can repeat a code across synonyms; keep the first. */
const dedupeByCode = (rows) => {
  const seen = {};
  const out = [];
  (rows || []).forEach((r) => {
    const key = (r && r.value) || (r && r.label) || "";
    if (!key || seen[key]) return;
    seen[key] = true;
    out.push(r);
  });
  return out;
};

/**
 * Collapse a panel's children that are the SAME TEST under two codes.
 *
 * LABT31 really does carry SLABT70 and SLABT396, both named "DIFFERENTIAL
 * SEGMENTED NEUTROPHILS" and both LOINC 26499-4 — duplication in the catalogue,
 * not in this list. Two identical rows in a picker is a defect: the doctor
 * cannot tell them apart, and ticking both orders the same test twice.
 *
 * The match must be on name AND LOINC. Name alone would fold together tests
 * that are genuinely distinct measurements sharing a label, and LOINC is the
 * clinical identity — same LOINC means either code names the same analyte, so
 * which one we send does not matter. A child with NO loinc is never folded.
 */
const dedupeSameTest = (rows) => {
  const seen = {};
  return (rows || []).filter((r) => {
    if (!r.loinc) return true;
    const key = `${String(r.label).trim().toLowerCase()}|${r.loinc}`;
    if (seen[key]) return false;
    seen[key] = true;
    return true;
  });
};

const contains = (text, q) => String(text || "").toLowerCase().indexOf(q) >= 0;

/**
 * Fold the panel search and the synonym search into one ordered panel list.
 *
 * Name matches lead: a doctor typing "complete blood" means the panel, and
 * burying it under twelve panels that merely contain a matching analyte would
 * be perverse. Panels reached only through a child follow, in the order the
 * synonym map returned them.
 */
const mergePanels = (named, synonymRows) => {
  const order = [];
  const byCode = {};

  (named || []).forEach((p) => {
    if (!p || !p.value || byCode[p.value]) return;
    byCode[p.value] = { value: p.value, label: p.label, viaName: true, hits: {} };
    order.push(p.value);
  });

  (synonymRows || []).forEach((s) => {
    if (!s || !s.mainTestCode || !s.value) return;
    if (!byCode[s.mainTestCode]) {
      byCode[s.mainTestCode] = {
        value: s.mainTestCode,
        label: s.mainTestName || s.mainTestCode,
        viaName: false,
        hits: {},
      };
      order.push(s.mainTestCode);
    }
    // Remember the ALIAS only as a last-resort label for this child code.
    byCode[s.mainTestCode].hits[s.value] = s.label || "";
  });

  return order.map((c) => byCode[c]);
};

const LabTestSearch = ({ onAdd, onDefaultInstruction, disabled }) => {
  const [query, setQuery] = useState("");
  const [panels, setPanels] = useState([]);
  const [truncated, setTruncated] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  /** code -> {label, value, isPanel} for everything currently ticked. */
  const [checked, setChecked] = useState({});
  /**
   * panel code -> `false` once the doctor has collapsed that panel. Absent
   * means untouched, and untouched means OPEN — see `isExpanded`.
   */
  const [expanded, setExpanded] = useState({});

  /* MOBILE ONLY. On a phone the results open in a bottom sheet instead of the
     anchored popover — see the branch before the return for why. These three
     lines, the `!mobile` on usePopover and that branch are the whole footprint
     here. Called unconditionally, as hooks must be. */
  const mobile = useIsMobile() && MOBILE.rxLabSearchSheet;
  const [sheetOpen, setSheetOpen] = useState(false);
  const sheetInputRef = useRef(null);

  const debounceRef = useRef(null);
  const serviceRef = useRef(null);
  // Bumped per search; a response from an older query is dropped rather than
  // painted over a newer one.
  const runRef = useRef(0);
  if (!serviceRef.current) serviceRef.current = new LabTestService();

  const dismiss = useCallback(() => setOpen(false), []);
  /* Not on mobile: the popover's outside-tap, scroll and resize dismissals are
     exactly what closed the list when a phone's keyboard was put away. */
  const { triggerRef, menuRef, menuStyle } = usePopover(open && !mobile, dismiss);

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  const runSearch = (raw) => {
    const run = (runRef.current += 1);
    const q = raw.trim().toLowerCase();
    const svc = serviceRef.current;

    // Both searches are independent; whichever lands second does the work.
    // The synonym map is enrichment, so its own failure resolves to [] rather
    // than failing the search.
    let named = null;
    let syn = null;

    const both = () => {
      if (named === null || syn === null) return;
      if (run !== runRef.current) return;

      const merged = mergePanels(named, syn);
      const shown = merged.slice(0, MAX_PANELS);
      setTruncated(merged.length - shown.length);

      if (!shown.length) {
        setPanels([]);
        setLoading(false);
        return;
      }

      // A panel whose own NAME matched renders immediately — its label came
      // from the catalogue and is trustworthy. A panel reached only through the
      // synonym map waits for the bulk call, because the only label we have for
      // it so far is the map's `mainTestName`, which is stale in production.
      // Showing it and then swapping the text would be worse than a short beat.
      setPanels(shown.map((p) => ({ ...p, tests: [], total: null, ready: p.viaName })));
      setLoading(false);

      svc.childrenBulk(
        shown.map((p) => p.value),
        (byPanel) => {
          if (run !== runRef.current) return;
          setPanels((prev) =>
            prev.map((p) => {
              const hit = byPanel[p.value];
              // Absent from the catalogue: a synonym row pointing at a code that
              // no longer exists. Drop it rather than render a name we can't
              // corroborate — `ready` stays false and it never paints.
              if (!hit) return p.viaName ? p : { ...p, dead: true };
              const all = dedupeSameTest(dedupeByCode(hit.tests));
              // The catalogue's own name WINS over the synonym map's, which is
              // stale for at least one code in production.
              const label = hit.name || p.label;
              if (!all.length) return { ...p, label, ready: true };
              // A child is "matching" if its own name contains the query or the
              // synonym map matched an alias of it.
              const tests = all.map((t) => ({
                ...t,
                matched: contains(t.label, q) || !!p.hits[t.value],
              }));
              return { ...p, label, tests, total: all.length, ready: true };
            })
          );
        }
      );
    };

    svc.search(
      raw,
      (rows) => {
        named = dedupeByCode(rows);
        both();
      },
      () => {
        // Non-fatal: an unreachable catalogue must not block the rest of the Rx.
        named = [];
        both();
      }
    );
    svc.synonyms(raw, (rows) => {
      syn = rows;
      both();
    });
  };

  const handleType = (e) => {
    const q = e.target.value;
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) {
      runRef.current += 1;
      setPanels([]);
      setTruncated(0);
      setOpen(false);
      return;
    }
    setLoading(true);
    setOpen(true);
    debounceRef.current = setTimeout(() => runSearch(q), DEBOUNCE_MS);
  };

  const toggle = (row, isPanel) =>
    setChecked((prev) => {
      const next = { ...prev };
      if (next[row.value]) delete next[row.value];
      else next[row.value] = { label: row.label, value: row.value, isPanel };
      return next;
    });

  const picked = Object.keys(checked);

  const commit = () => {
    const rows = picked.map((k) => checked[k]);
    if (!rows.length) return;
    onAdd(rows.map((r) => ({ label: r.label, value: r.value })));
    // The catalogue's default advice exists for PANELS only (`getby` takes a
    // LABT code), so it is fetched for those and skipped for sub-tests.
    if (onDefaultInstruction) {
      rows
        .filter((r) => r.isPanel)
        .forEach((r) =>
          serviceRef.current.info(
            r.value,
            (info) => info && info.instruction && onDefaultInstruction(r.value, info.instruction),
            () => {}
          )
        );
    }
    setChecked({});
    setQuery("");
    setPanels([]);
    setTruncated(0);
    setExpanded({});
    setOpen(false);
  };

  const Box = ({ on }) => (
    <span className={`rx-ac-box${on ? " is-on" : ""}`} aria-hidden="true">
      <Tick />
    </span>
  );
  Box.propTypes = { on: PropTypes.bool };

  /**
   * Every panel on screen shows its WHOLE child list (Nittin, 2026-07-29).
   *
   * Not just the children that matched the query: if "hem" surfaced Hemoglobin
   * and therefore surfaced Complete Blood Count, the doctor is looking at CBC
   * and needs to see what CBC contains — showing 5 of its 28 tests states
   * something false about the panel. How a panel was FOUND does not change what
   * a panel IS.
   *
   * The count collapses a panel to just the query's own matches, so a 38-test
   * Urine Routine can be folded away rather than scrolled past.
   */
  const isExpanded = (p) => expanded[p.value] !== false;

  const visibleTests = (p) => (isExpanded(p) ? p.tests : p.tests.filter((t) => t.matched));

  /* The magnifier, drawn in both layouts. */
  const searchIcon = (
    <svg
      className="rx-ac-icon"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <circle cx="9" cy="9" r="6" />
      <path d="M14 14l4 4" strokeLinecap="round" />
    </svg>
  );

  /* The result list, its truncation note and its help line — rendered by the
     desktop popover and the mobile sheet alike, so the two can never disagree
     about what a search shows. Unchanged markup, lifted out of the popover. */
  const results = (
    <>
      <div className="rx-ac-list" role="group">
        {loading && <p className="rx-ac-empty">Searching&hellip;</p>}
        {!loading && panels.filter((p) => p.ready && !p.dead).length === 0 && (
          <p className="rx-ac-empty">
            {panels.length ? "Loading\u2026" : "No investigations match that."}
          </p>
        )}

        {!loading &&
          panels.filter((p) => p.ready && !p.dead).map((p) => (
            <div key={p.value}>
              <div className="rx-ac-panel">
                <button
                  type="button"
                  className="rx-ac-row is-panel"
                  aria-pressed={!!checked[p.value]}
                  onClick={() => toggle(p, true)}
                >
                  <Box on={!!checked[p.value]} />
                  <span className="rx-ac-name">{p.label}</span>
                </button>
                {p.total ? (
                  <button
                    type="button"
                    className="rx-ac-meta"
                    aria-expanded={isExpanded(p)}
                    onClick={() =>
                      setExpanded((prev) => ({ ...prev, [p.value]: !isExpanded(p) }))
                    }
                  >
                    Panel &middot; {p.total} {p.total === 1 ? "test" : "tests"}
                  </button>
                ) : null}
              </div>

              {visibleTests(p).map((t) => (
                <button
                  key={t.value}
                  type="button"
                  className="rx-ac-row is-test"
                  aria-pressed={!!checked[t.value]}
                  onClick={() => toggle(t, false)}
                >
                  <Box on={!!checked[t.value]} />
                  <span className="rx-ac-name">{t.label}</span>
                </button>
              ))}
            </div>
          ))}
      </div>

      {truncated > 0 && (
        <p className="rx-ac-note">
          Showing the first {MAX_PANELS} panels &mdash; {truncated} more match. Type
          more to narrow it down.
        </p>
      )}

      <p className="rx-ac-help">
        Selecting the panel adds all its tests; tests can also be added
        individually.
      </p>
    </>
  );

  const addButton = (onClick) => (
    <button
      type="button"
      className="rx-ac-add"
      disabled={!picked.length}
      onClick={onClick}
    >
      Add selected ({picked.length})
    </button>
  );

  /* MOBILE — the results in a bottom sheet, not a popover.

     The popover dismisses on an outside tap, on any scroll and on resize. On a
     phone those are exactly how a person puts the keyboard away to read the
     list: the tap outside closed it, and on Android the keyboard closing is
     itself a resize. Worse, "Add selected" sits at the popover's foot, behind
     the keyboard — so reaching it closed the list and stranded the ticks.

     The sheet closes only on ✕ or its backdrop. Its body scrolls, "Add
     selected" is its footer (kept above the keyboard by BottomSheet), and it
     stops under row 1 like every other sheet on this screen.

     THE KEYBOARD OPENS ON THE FIRST TAP. iOS raises it only for a focus call
     made inside the tap itself, and the sheet's input is hidden
     (`visibility: hidden`) until the sheet opens — so the open is flushed
     synchronously, the input becomes focusable in the same tick, and focus
     lands before the handler returns. */
  if (mobile) {
    const openSheet = () => {
      if (disabled) return;
      ReactDOM.flushSync(() => setSheetOpen(true));
      if (sheetInputRef.current) sheetInputRef.current.focus();
    };

    return (
      <div className="rx-ac">
        <button
          type="button"
          className="rx-ac-field zym-ac-open"
          onClick={openSheet}
          disabled={disabled}
          aria-haspopup="dialog"
        >
          {searchIcon}
          <span className={`zym-ac-open-text${query ? "" : " is-placeholder"}`}>
            {query || "Search and add investigations…"}
          </span>
        </button>

        <BottomSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="Add investigation"
          className="zym-sheet-labsearch"
          clearTopBar
          footer={addButton(() => {
            commit();
            setSheetOpen(false);
          })}
        >
          <div className="rx-ac-field zym-ac-sheet-field">
            {searchIcon}
            <input
              ref={sheetInputRef}
              type="text"
              value={query}
              onChange={handleType}
              placeholder="Search and add investigations&hellip;"
              aria-label="Search and add investigations"
            />
          </div>
          {query.trim() ? (
            results
          ) : (
            <p className="rx-ac-empty">Start typing to search investigations.</p>
          )}
        </BottomSheet>
      </div>
    );
  }

  return (
    <div className="rx-ac">
      <div className="rx-ac-field" ref={triggerRef}>
        {searchIcon}
        <input
          type="text"
          value={query}
          onChange={handleType}
          onFocus={() => query && panels.length > 0 && setOpen(true)}
          placeholder="Search and add investigations&hellip;"
          aria-label="Search and add investigations"
          disabled={disabled}
        />
      </div>

      {open &&
        ReactDOM.createPortal(
          <div className="rx-ac-menu" ref={menuRef} style={menuStyle} role="dialog" aria-label="Add investigation">
            {results}

            <div className="rx-ac-foot">{addButton(commit)}</div>
          </div>,
          document.body
        )}
    </div>
  );
};

LabTestSearch.propTypes = {
  /** Called with an ARRAY of {label, value} — one per ticked row. */
  onAdd: PropTypes.func.isRequired,
  /** `(ltCode, text)` — the catalogue's default advice for a picked PANEL. */
  onDefaultInstruction: PropTypes.func,
  disabled: PropTypes.bool,
};

export default LabTestSearch;
